/**
 * Theme store. Persists to localStorage; falls back to the OS preference.
 */
import { create } from "zustand";

interface ThemeState {
  isDark: boolean;
  toggle: () => void;
  set: (dark: boolean) => void;
}

const LS_KEY = "theme";

function initial(): boolean {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved) return saved === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

function apply(dark: boolean): void {
  try {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem(LS_KEY, dark ? "dark" : "light");
  } catch {
    /* SSR-safe no-op */
  }
}

const start = initial();
if (typeof document !== "undefined") apply(start);

export const useTheme = create<ThemeState>((set, get) => ({
  isDark: start,
  toggle: () => {
    const next = !get().isDark;
    apply(next);
    set({ isDark: next });
  },
  set: (dark) => {
    apply(dark);
    set({ isDark: dark });
  },
}));
